import Uppy from "@uppy/core";
import { fetchUploadStatuses } from "./file-status-monitor";
import {
    formatUploadStatusLabel,
    groupPendingUploadPollTargets,
} from "./file-upload-polling";

function createFileUploaderData(Alpine, overrides = {}) {
    return {
        uppy: null,
        files: [],
        uploading: false,
        autoUploadOnAdd: false,
        selectedRegion: "west",
        enabledRegions: [],
        pollTimerId: null,
        visibilityHandler: null,
        focusHandler: null,
        config: {
            intentUrl: null,
            attachableType: null,
            attachableId: null,
            csrfToken: null,
            defaultRegion: "west",
            enabledRegions: [],
            autoUploadOnAdd: false,
            commitMode: "immediate",
        },
        ...overrides,
        init() {
            this.loadConfig();

            this.selectedRegion = this.config.defaultRegion ?? "west";
            this.enabledRegions = Array.isArray(this.config.enabledRegions)
                ? this.config.enabledRegions
                : [];
            this.autoUploadOnAdd = Boolean(this.config.autoUploadOnAdd);

            this.uppy = new Uppy({
                autoProceed: false,
                allowMultipleUploadBatches: true,
                restrictions: {
                    maxNumberOfFiles: 25,
                },
            });

            this.uppyRaw().addUploader(async (fileIDs) => {
                this.uploading = true;
                this.dispatchState();

                for (const fileID of fileIDs) {
                    const file = this.uppyRaw().getFile(fileID);
                    if (!file) {
                        continue;
                    }

                    try {
                        this.setFileState(fileID, { status: "preparing", error: null });
                        const intent = await this.createIntent(file);

                        this.setFileState(fileID, {
                            status: "uploading",
                            fileId: intent.file_id,
                            statusUrl: intent.status_url,
                            abandonUrl: intent.abandon?.url || null,
                        });

                        await this.uploadBinary(file, intent.upload);
                        this.setFileState(fileID, { status: "finalizing" });

                        const finalizeResponse = await this.postJson(intent.finalize.url, {
                            token: intent.finalize.token,
                            commit_mode: this.config.commitMode || "immediate",
                        });

                        this.setFileState(fileID, {
                            status: finalizeResponse.status,
                            scanStatus: finalizeResponse.scan_status,
                        });

                        this.queueStatusPoll();
                    } catch (error) {
                        this.setFileState(fileID, {
                            status: "failed",
                            error: error instanceof Error ? error.message : "Upload failed",
                        });
                    }
                }

                this.uploading = false;
                this.dispatchState();
            });

            this.registerPollLifecycleHandlers();
            this.dispatchState();
            this.$dispatch("file-uploader-ready", {
                abandonUploads: this.abandonUploads.bind(this),
                resetFiles: this.resetFiles.bind(this),
                addFiles: this.addFiles.bind(this),
            });
        },
        destroy() {
            this.stopStatusPolling();

            if (this.visibilityHandler) {
                document.removeEventListener("visibilitychange", this.visibilityHandler);
                this.visibilityHandler = null;
            }

            if (this.focusHandler) {
                window.removeEventListener("focus", this.focusHandler);
                this.focusHandler = null;
            }
        },
        uppyRaw() {
            return Alpine.raw(this.uppy);
        },
        registerPollLifecycleHandlers() {
            this.visibilityHandler = () => {
                if (this.hasPendingStatusPolls() && this.isPollingInteractive()) {
                    this.queueStatusPoll(250);
                }
            };

            this.focusHandler = () => {
                if (this.hasPendingStatusPolls() && this.isPollingInteractive()) {
                    this.queueStatusPoll(250);
                }
            };

            document.addEventListener("visibilitychange", this.visibilityHandler);
            window.addEventListener("focus", this.focusHandler);
        },
        loadConfig() {
            const encoded = this.$el.dataset.uploaderConfig;
            if (!encoded) {
                return;
            }

            try {
                const parsed = JSON.parse(encoded);
                if (typeof parsed === "object" && parsed !== null) {
                    this.config = {
                        ...this.config,
                        ...parsed,
                    };
                }
            } catch (_error) {
                this.config = { ...this.config };
            }
        },
        browse() {
            if (this.$refs.input) {
                this.$refs.input.click();
            }
        },
        onInputChange(event) {
            this.addFiles(event.target.files);
            event.target.value = "";
        },
        onDrop(event) {
            this.addFiles(event.dataTransfer?.files ?? []);
        },
        addFiles(fileList) {
            let addedFilesCount = 0;

            for (const file of fileList) {
                const id = this.uppyRaw().addFile({
                    name: file.name,
                    type: file.type || "application/octet-stream",
                    data: file,
                    source: "local",
                });

                this.files.push({
                    uppyId: id,
                    name: file.name,
                    size: file.size,
                    status: "queued",
                    scanStatus: null,
                    error: null,
                    fileId: null,
                    statusUrl: null,
                    abandonUrl: null,
                });

                addedFilesCount += 1;
            }

            if (this.autoUploadOnAdd && addedFilesCount > 0) {
                this.$nextTick(() => this.startUpload());
            }

            this.dispatchState();
        },
        startUpload() {
            if (this.uploading || this.files.length === 0) {
                return;
            }

            this.uppyRaw().upload();
        },
        async createIntent(file) {
            const payload = {
                attachable_type: this.config.attachableType,
                attachable_id: this.config.attachableId,
                original_name: file.name,
                byte_size: file.size,
                content_type_claimed: file.type || null,
                region_preference: this.selectedRegion,
            };

            return await this.postJson(this.config.intentUrl, payload);
        },
        async uploadBinary(file, uploadConfig) {
            const method = uploadConfig.method || "PUT";
            const headers = uploadConfig.headers || {};

            await new Promise((resolve, reject) => {
                const request = new XMLHttpRequest();
                request.open(method, uploadConfig.url, true);

                for (const [key, value] of Object.entries(headers)) {
                    request.setRequestHeader(key, value);
                }

                if (this.config.csrfToken && !headers["X-CSRF-TOKEN"]) {
                    request.setRequestHeader("X-CSRF-TOKEN", this.config.csrfToken);
                }

                request.upload.onprogress = (event) => {
                    if (event.lengthComputable) {
                        const progress = Math.round((event.loaded / event.total) * 100);
                        this.setFileState(file.id, { status: `uploading ${progress}%` });
                    }
                };

                request.onload = () => {
                    if (request.status >= 200 && request.status < 300) {
                        resolve();
                        return;
                    }

                    reject(new Error(`Upload failed with status ${request.status}`));
                };

                request.onerror = () => reject(new Error("Upload request failed"));
                request.send(file.data);
            });
        },
        async postJson(url, payload) {
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    "X-CSRF-TOKEN": this.config.csrfToken,
                },
                body: JSON.stringify(payload),
            });

            const body = await response.json().catch(() => ({}));
            if (!response.ok) {
                const message = body?.message || `Request failed with status ${response.status}`;
                throw new Error(message);
            }

            return body.data;
        },
        setFileState(uppyId, updates) {
            this.files = this.files.map((entry) => {
                if (entry.uppyId !== uppyId) {
                    return entry;
                }

                return {
                    ...entry,
                    ...updates,
                };
            });

            this.dispatchState();
        },
        statusLabel(file) {
            return formatUploadStatusLabel(file.status);
        },
        stopStatusPolling() {
            if (this.pollTimerId) {
                window.clearTimeout(this.pollTimerId);
                this.pollTimerId = null;
            }
        },
        queueStatusPoll(delay = 2500) {
            if (!this.hasPendingStatusPolls()) {
                this.stopStatusPolling();
                return;
            }

            this.stopStatusPolling();
            this.pollTimerId = window.setTimeout(() => this.tickStatusPoll(), delay);
        },
        hasPendingStatusPolls() {
            return groupPendingUploadPollTargets(this.files).size > 0;
        },
        isPollingInteractive() {
            return document.visibilityState === "visible" && document.hasFocus();
        },
        async tickStatusPoll() {
            if (!this.hasPendingStatusPolls()) {
                this.stopStatusPolling();
                return;
            }

            if (!this.isPollingInteractive()) {
                this.queueStatusPoll(10000);
                return;
            }

            const pollGroups = groupPendingUploadPollTargets(this.files);

            for (const [statusUrl, fileIds] of pollGroups.entries()) {
                try {
                    const statuses = await fetchUploadStatuses(statusUrl, fileIds);

                    statuses.forEach((latestStatus) => {
                        const fileId = Number.parseInt(
                            String(latestStatus?.file_id || "0"),
                            10,
                        );

                        if (fileId <= 0) {
                            return;
                        }

                        const file = this.files.find((entry) => entry.fileId === fileId);

                        if (!file) {
                            return;
                        }

                        this.setFileState(file.uppyId, {
                            status: latestStatus.status || "unknown",
                            scanStatus: latestStatus.scan_status || null,
                        });
                    });
                } catch (_error) {
                    this.queueStatusPoll(5000);
                    return;
                }
            }

            if (!this.hasPendingStatusPolls()) {
                this.stopStatusPolling();
                return;
            }

            this.queueStatusPoll();
        },
        async abandonUploads() {
            if (this.uploading) {
                throw new Error("Please wait for uploads to finish before cancelling.");
            }

            const stagedFiles = this.files.filter(
                (entry) => entry.status === "staged" && entry.fileId && entry.abandonUrl,
            );

            for (const file of stagedFiles) {
                await this.postJson(file.abandonUrl, {});
            }

            this.resetFiles();
        },
        async removeFile(uppyId) {
            const file = this.files.find((entry) => entry.uppyId === uppyId);

            if (!file) {
                return;
            }

            const status = String(file.status || "");
            const isInFlight = ["preparing", "uploading", "finalizing"].some((entryStatus) =>
                status.startsWith(entryStatus),
            );

            if (isInFlight) {
                return;
            }

            if (status === "staged" && file.fileId && file.abandonUrl) {
                await this.postJson(file.abandonUrl, {});
            }

            this.removeQueued(uppyId);
        },
        removeQueued(uppyId) {
            this.uppyRaw().removeFile(uppyId);
            this.files = this.files.filter((entry) => entry.uppyId !== uppyId);
            this.queueStatusPoll();
            this.dispatchState();
        },
        resetFiles() {
            const existingFiles = [...this.files];

            for (const entry of existingFiles) {
                const uppyFile = this.uppyRaw().getFile(entry.uppyId);
                if (uppyFile) {
                    this.uppyRaw().removeFile(entry.uppyId);
                }
            }

            this.files = [];
            this.stopStatusPolling();
            this.dispatchState();
        },
        dispatchState() {
            const inFlightStatuses = ["queued", "preparing", "uploading", "finalizing"];
            const hasUploadsInFlight = this.files.some((entry) =>
                inFlightStatuses.some((status) =>
                    String(entry.status || "").startsWith(status),
                ),
            );

            this.$dispatch("file-uploader-state-changed", {
                uploading: this.uploading,
                hasUploadsInFlight,
                filesCount: this.files.length,
            });
        },
    };
}

export function registerFileUploader(Alpine) {
    Alpine.data("fileUploader", () => createFileUploaderData(Alpine));
    Alpine.data("conceptUploader", () => createFileUploaderData(Alpine));
}

export function registerConceptUploader(Alpine) {
    registerFileUploader(Alpine);
}
